import { Link } from "react-router-dom";
import { Check, Minus, Ambulance } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";

const plans = [
  {
    name: "Pay-per-ride",
    subtitle: "Best for one-off needs",
    price: "From $49",
    priceSuffix: "/ trip",
    highlight: false,
    cta: "Book a ride",
    features: [
      { t: "On-demand BLS ambulance", in: true },
      { t: "Real-time GPS tracking", in: true },
      { t: "Transparent upfront fare", in: true },
      { t: "24/7 support line", in: true },
      { t: "ICU / ALS upgrade", in: false },
      { t: "Corporate dashboard", in: false },
    ],
  },
  {
    name: "Family Care",
    subtitle: "Most chosen by households",
    price: "$19",
    priceSuffix: "/ month",
    highlight: true,
    cta: "Start Family Care",
    features: [
      { t: "Priority dispatch for 4 members", in: true },
      { t: "20% off every ride", in: true },
      { t: "Scheduled transfers", in: true },
      { t: "ALS / ICU upgrade available", in: true },
      { t: "Dedicated relationship manager", in: false },
      { t: "Custom SLAs", in: false },
    ],
  },
  {
    name: "Enterprise",
    subtitle: "For corporates, hospitals, insurers",
    price: "Custom",
    priceSuffix: "",
    highlight: false,
    cta: "Talk to sales",
    features: [
      { t: "Unlimited members", in: true },
      { t: "API & dispatch integration", in: true },
      { t: "Dedicated fleet options", in: true },
      { t: "ALS / ICU / neonatal coverage", in: true },
      { t: "Custom SLAs & reporting", in: true },
      { t: "24/7 account support", in: true },
    ],
  },
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <PageHeader
        eyebrow="Pricing"
        title="Simple, transparent, and patient-friendly."
        description="No hidden charges. You see the exact fare before you confirm, and every receipt is itemised. Save more with Family Care or Enterprise plans."
      />

      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((p, i) => (
              <div
                key={p.name}
                data-testid={`pricing-plan-${i}`}
                className={`relative rounded-3xl p-8 sm:p-10 border transition-all ${
                  p.highlight
                    ? "bg-[#14241F] text-white border-[#14241F] shadow-2xl scale-[1.02]"
                    : "bg-white text-[#14241F] border-[#D5DDD7] hover:shadow-lg"
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-8 inline-flex items-center bg-[#E5383B] text-white text-xs font-semibold tracking-[0.2em] uppercase px-3 py-1 rounded-full">
                    Most popular
                  </span>
                )}
                <h3 className="text-2xl font-[Outfit] font-semibold">{p.name}</h3>
                <p className={`mt-1 text-sm ${p.highlight ? "text-[#D5DDD7]/80" : "text-[#495D56]"}`}>{p.subtitle}</p>
                <div className="mt-8 flex items-baseline gap-1">
                  <span className="text-5xl font-[Outfit] font-bold">{p.price}</span>
                  {p.priceSuffix && <span className={`${p.highlight ? "text-[#D5DDD7]/80" : "text-[#495D56]"}`}>{p.priceSuffix}</span>}
                </div>
                <ul className="mt-8 space-y-3">
                  {p.features.map((f) => (
                    <li key={f.t} className="flex items-start gap-3 text-sm">
                      {f.in ? (
                        <Check className={`h-5 w-5 flex-shrink-0 ${p.highlight ? "text-[#E5383B]" : "text-[#1A3A32]"}`} />
                      ) : (
                        <Minus className={`h-5 w-5 flex-shrink-0 ${p.highlight ? "text-white/30" : "text-[#D5DDD7]"}`} />
                      )}
                      <span className={f.in ? "" : "opacity-50 line-through"}>{f.t}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`pricing-cta-${i}`}
                  className={`mt-10 inline-flex w-full items-center justify-center rounded-lg px-6 py-3.5 font-semibold transition-colors ${
                    p.highlight
                      ? "bg-[#E5383B] text-white hover:bg-[#BA1826]"
                      : "border-2 border-[#1A3A32] text-[#1A3A32] hover:bg-[#EDF1ED]"
                  }`}
                >
                  {p.cta}
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-16 rounded-3xl bg-[#EDF1ED] p-8 sm:p-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <div className="flex items-start gap-4">
                <Ambulance className="h-10 w-10 text-[#1A3A32]" />
                <div>
                  <h3 className="text-2xl font-[Outfit] font-semibold text-[#14241F]">What's included in every ride</h3>
                  <p className="mt-2 text-[#495D56]">Insurance-grade documentation, trained crew, certified equipment, and a 24/7 support line. Final price depends on service level (BLS / ALS / ICU), distance and waiting time.</p>
                </div>
              </div>
            </div>
            <div className="flex md:justify-end items-center">
              <Link to="/services" data-testid="pricing-services-link" className="inline-flex items-center bg-[#1A3A32] text-white hover:bg-[#14241F] rounded-lg px-6 py-3 font-semibold">
                Compare services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
